import { BaseService } from '@common/http-layer/service/base.service'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { withDefaultGamificationMeta } from '@communities/common-layer/utils/gamification.utils'
import type { CommunityUserGroup } from '../../community-user-layer/types/community-user'
import type { CourseWithAccess } from '../types/course-access'

function withDefaultMemberGamificationMeta(member: CommunityUserGroup): CommunityUserGroup {
  return {
    ...member,
    gamificationMeta: withDefaultGamificationMeta(member.gamificationMeta),
  }
}

export interface PaginationParams {
  limit?: number
  skip?: number
  offset?: number
  search?: string
}

export interface GroupMemberRoleFilters {
  memberRole?: string[]
}

export interface MemberDetailsParams {
  limit?: number
  skip?: number
  search?: string
  memberStatus?: string
  memberRole?: string
  /** When true, API returns member email (for group admins only). */
  showEmail?: boolean
  /** Filter ACTIVE members who hold this badge template id. */
  badgeTemplateId?: string
}

/**
 * Members service - handles all member-related API calls
 * Base URL: COMMUNITIES_SERVER_URL
 * Base Path: /{locationId}/groups/{groupId}/users
 */
export class MembersService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '',
    })
  }

  /**
   * Build location-aware endpoint
   */
  private endpoint(path: string = '', locationId?: string): string {
    if (locationId) {
      return `/${locationId}/groups${path}`
    }
    const { store: state } = useLocation()

    if (!state.locationId) {
      console.error('[MembersService.endpoint] Location ID is missing from state:', state)
      throw new Error('Location ID is required')
    }

    return `/${state.locationId}/groups${path}`
  }

  /**
   * Get group members by specific role (invited, banned, requested, etc.)
   */
  async getGroupMembersDetails(groupId: string, locationId: string, params?: MemberDetailsParams): Promise<CommunityUserGroup[]> {
    try {
      const limit = params?.limit || 20
      const skip = params?.skip || 0
      // Calculate pageNo from skip and limit: pageNo = (skip / limit) + 1
      // skip=0, limit=20 -> pageNo=1
      // skip=20, limit=20 -> pageNo=2
      // skip=40, limit=20 -> pageNo=3
      const pageNo = Math.floor(skip / limit) + 1

      const queryParams = params
        ? {
            limit,
            skip,
            searchText: params.search || '',
            showEmail: params.showEmail ?? false,
            pageNo,
            // Only include memberRole if it has a value
            ...(params.memberRole && { memberRole: params.memberRole }),
            // Only include memberStatus if it has a value
            ...(params.memberStatus && { memberStatus: params.memberStatus }),
            ...(params.badgeTemplateId && { badgeTemplateId: params.badgeTemplateId }),
          }
        : {}

      const endpoint = this.endpoint(`/${groupId}/users/details`, locationId)

      const { data } = await this.get<CommunityUserGroup[]>(endpoint, { params: queryParams, locationId, groupId })

      return (data || []).map(withDefaultMemberGamificationMeta)
    } catch (error) {
      console.error(`[MembersService.getGroupMembersDetails] ❌ Error fetching ${params?.memberRole || 'all'} members:`, error)
      throw error
    }
  }

  /**
   * Get count of pending member requests (for Requested tab badge).
   * GET /{locationId}/groups/{groupId}/users/request/count
   */
  async getGroupMemberRequestCount(groupId: string, locationId: string): Promise<{ count: number }> {
    try {
      const endpoint = this.endpoint(`/${groupId}/users/request/count`, locationId)
      const { data } = await this.get<{ count: number }>(endpoint, { locationId, groupId })
      return data ?? { count: 0 }
    } catch (error) {
      console.error('[MembersService.getGroupMemberRequestCount] ❌ Error fetching request count:', error)
      throw error
    }
  }

  /**
   * Get group members by specific roles (e.g., ADMIN, OWNER) via the /list endpoint
   */
  async getGroupMembersList(groupId: string, locationId: string, params?: GroupMemberRoleFilters): Promise<CommunityUserGroup[]> {
    try {
      const queryParams: Record<string, unknown> = { ...params }

      if (params?.memberRole && params.memberRole.length > 0) {
        queryParams.memberRole = params.memberRole.join(',')
      }

      const endpoint = this.endpoint(`/${groupId}/users/list`, locationId)

      const { data } = await this.get<CommunityUserGroup[]>(endpoint, {
        params: queryParams,
        locationId,
        groupId,
      })

      return (data || []).map(withDefaultMemberGamificationMeta)
    } catch (error) {
      console.error(`[MembersService.getGroupMembersList] ❌ Error fetching members by roles:`, error)
      throw error
    }
  }

  /**
   * Update a group member's role
   */
  async updateGroupMember(groupId: string, locationId: string, userId: string, role: string): Promise<Record<string, unknown>> {
    try {
      const { data } = await this.patch(
        this.endpoint(`/${groupId}/users/${userId}`, locationId),
        { contactId: userId, role },
        { locationId, groupId }
      )
      return data
    } catch (error) {
      console.error(`[MembersService.updateGroupMember] ❌ Error updating group member:`, error)
      throw error
    }
  }

  /**
   * Ban a group member (optional deleteContent: delete all content created by the user).
   */
  async banGroupMember(groupId: string, locationId: string, userId: string, deleteContent?: boolean): Promise<Record<string, unknown>> {
    const payload: { contactId: string; status: string; deleteContent?: boolean } = {
      contactId: userId,
      status: 'Banned',
    }
    if (deleteContent === true) {
      payload.deleteContent = true
    }
    const { data } = await this.patch(this.endpoint(`/${groupId}/users/${userId}`, locationId), payload, { locationId, groupId })
    return data
  }

  /**
   * Unban a group member
   */
  async unbanGroupMember(groupId: string, locationId: string, userId: string): Promise<Record<string, unknown>> {
    const { data } = await this.patch(
      this.endpoint(`/${groupId}/users/${userId}`, locationId),
      { contactId: userId, status: 'Active' },
      { locationId, groupId }
    )
    return data
  }

  /**
   * Withdraw a member invitation
   */
  async withdrawMemberInvitation(
    groupId: string,
    locationId: string,
    userId: string,
    invitationId: string
  ): Promise<Record<string, unknown>> {
    const { data } = await this.delete(this.endpoint(`/${groupId}/invitations/${invitationId}`, locationId), { locationId, groupId })
    return data
  }

  /**
   * Fetch membership questions form
   */
  async fetchMembershipQuestionsForm(groupId: string, locationId: string, formId: string): Promise<Record<string, unknown>> {
    const { data } = await this.get(this.endpoint(`/${groupId}/forms/${formId}`, locationId))
    return data
  }

  /**
   * Approve a member request
   */
  async approveMemberRequest(
    groupId: string,
    locationId: string,
    userId: string,
    role: string = 'MEMBER'
  ): Promise<Record<string, unknown>> {
    try {
      const { data } = await this.patch(
        this.endpoint(`/${groupId}/users/${userId}`, locationId),
        { contactId: userId, role, status: 'Active' },
        { locationId, groupId }
      )
      return data
    } catch (error) {
      console.error(`[MembersService.approveMemberRequest] ❌ Error approving member request:`, error)
      throw error
    }
  }

  /**
   * Reject a member request (DELETE user, same as remove).
   */
  async rejectMemberRequest(groupId: string, locationId: string, userId: string): Promise<Record<string, unknown>> {
    try {
      const { data } = await this.delete(this.endpoint(`/${groupId}/users/${userId}`, locationId), {
        locationId,
        groupId,
      })
      return data
    } catch (error) {
      console.error(`[MembersService.rejectMemberRequest] ❌ Error rejecting member request:`, error)
      throw error
    }
  }

  /**
   * Remove a member from group (optional removeGamificationMeta query param when also wiping points).
   */
  async removeGroupMember(
    groupId: string,
    locationId: string,
    userId: string,
    removeGamificationMeta?: boolean
  ): Promise<Record<string, unknown>> {
    try {
      const requestConfig: { locationId: string; groupId: string; params?: { removeGamificationMeta: boolean } } = {
        locationId,
        groupId,
      }
      if (removeGamificationMeta === true) {
        requestConfig.params = { removeGamificationMeta: true }
      }
      const { data } = await this.delete(this.endpoint(`/${groupId}/users/${userId}`, locationId), requestConfig)
      return data
    } catch (error) {
      console.error(`[MembersService.removeGroupMember] ❌ Error removing member from group:`, error)
      throw error
    }
  }

  /**
   * Reset a member's gamification meta (leaderboard points) without removing them from the group.
   * DELETE /{locationId}/groups/{groupId}/users/{userId}/gamification-meta
   */
  async deleteMemberGamificationMeta(groupId: string, locationId: string, userId: string): Promise<Record<string, unknown>> {
    try {
      const { data } = await this.delete(this.endpoint(`/${groupId}/users/${userId}/gamification-meta`, locationId), {
        locationId,
        groupId,
      })
      return data
    } catch (error) {
      console.error(`[MembersService.deleteMemberGamificationMeta] ❌ Error wiping member gamification meta:`, error)
      throw error
    }
  }

  // ===== COURSE ACCESS (manage courses for a member) =====

  /**
   * GET /{locationId}/groups/{groupId}/user/courses/member/{contactId}
   * Returns all courses for this member with access flags.
   */
  async getUserCoursesByContactId(groupId: string, locationId: string, contactId: string): Promise<{ courses: CourseWithAccess[] }> {
    const { data } = await this.get<{ courses: CourseWithAccess[] }>(
      this.endpoint(`/${groupId}/user/courses/member/${contactId}`, locationId),
      { params: { limit: 500 }, locationId, groupId }
    )
    return data ?? { courses: [] }
  }

  /**
   * POST /{locationId}/groups/{groupId}/user/courses/grant-access
   * Grants manual course access to a member.
   */
  async grantCourseAccess(groupId: string, locationId: string, contactId: string, productIds: string[]): Promise<Record<string, unknown>> {
    const { data } = await this.post(
      this.endpoint(`/${groupId}/user/courses/grant-access`, locationId),
      { contactId, productIds },
      { locationId, groupId }
    )
    return data
  }

  /**
   * DELETE /{locationId}/groups/{groupId}/user/courses/revoke-access
   * Revokes manual course access from a member.
   */
  async revokeCourseAccess(groupId: string, locationId: string, contactId: string, productIds: string[]): Promise<Record<string, unknown>> {
    const { data } = await this.delete(this.endpoint(`/${groupId}/user/courses/revoke-access`, locationId), {
      body: { contactId, productIds },
      locationId,
      groupId,
    })
    return data
  }

  /**
   * Submit membership answers (joining questionnaire)
   * POST /{locationId}/groups/{groupId}/users/joining-questionnaire
   */
  async submitMembershipAnswers(
    groupId: string,
    locationId: string,
    formId: string,
    contactId: string,
    answers: (string | boolean[])[]
  ): Promise<CommunityUserGroup> {
    try {
      const { data } = await this.post<CommunityUserGroup>(
        this.endpoint(`/${groupId}/users/joining-questionnaire`, locationId),
        { answers, formId, contactId },
        { locationId, groupId }
      )
      return data
    } catch (error) {
      console.error(`[MembersService.submitMembershipAnswers] ❌ Error submitting membership answers:`, error)
      throw error
    }
  }
}
